import React from 'react';
import Icon from 'components/AppIcon';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { SUB_SCORE_SPARKLINES } from './esgData';

function GaugeSVG({ score }) {
  const radius = 80;
  const cx = 110;
  const cy = 110;
  const startAngle = 210;
  const endAngle = 330;
  const totalArc = 360 - startAngle + endAngle;
  const filled = (score / 100) * totalArc;

  const toRad = (deg) => (deg * Math.PI) / 180;
  const arcPath = (cx, cy, r, startDeg, endDeg) => {
    const start = toRad(startDeg);
    const end = toRad(endDeg);
    const x1 = cx + r * Math.cos(start);
    const y1 = cy + r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy + r * Math.sin(end);
    const largeArc = endDeg - startDeg > 180 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  const color = score >= 70 ? '#16a34a' : score >= 50 ? '#d97706' : '#dc2626';
  const filledEnd = startAngle + filled;

  return (
    <svg viewBox="0 0 220 180" className="w-full" style={{ maxWidth: 220 }}>
      {/* Track */}
      <path
        d={arcPath(cx, cy, radius, startAngle, startAngle + totalArc)}
        fill="none"
        stroke="#e2e8f0"
        strokeWidth={16}
        strokeLinecap="round"
      />
      {/* Filled */}
      <path
        d={arcPath(cx, cy, radius, startAngle, filledEnd)}
        fill="none"
        stroke={color}
        strokeWidth={16}
        strokeLinecap="round"
      />
      {/* Score text */}
      <text x={cx} y={cy - 8} textAnchor="middle" fontSize={38} fontWeight={700} fill={color} fontFamily="inherit">{score}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize={12} fill="#64748b" fontFamily="inherit">/100</text>
      <text x={cx} y={cy + 32} textAnchor="middle" fontSize={10} fill="#94a3b8" fontFamily="inherit">Portfolio ESG Score</text>
    </svg>
  );
}

function Sparkline({ data, color }) {
  const chartData = data?.map((v, i) => ({ v, i }));
  return (
    <ResponsiveContainer width="100%" height={32}>
      <LineChart data={chartData} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={1.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

const SUB_SCORES = [
  { key: 'environmental', label: 'Environmental', score: 71, color: '#16a34a', stat: '142,000 tCO₂e avoided', sparkKey: 'environmental' },
  { key: 'social', label: 'Social', score: 78, color: '#2563eb', stat: '3,240 jobs supported', sparkKey: 'social' },
  { key: 'governance', label: 'Governance', score: 73, color: '#475569', stat: '94% board compliance', sparkKey: 'governance' },
];

export default function ESGScoreGauge({ score = 74, change = 2.3, changePeriod = 'Q3 2025' }) {
  return (
    <div className="card flex flex-col lg:flex-row gap-6" style={{ padding: '24px' }}>
      {/* Gauge */}
      <div className="flex flex-col items-center justify-center min-w-[200px]" style={{ flex: '0 0 220px' }}>
        <GaugeSVG score={score} />
        <div className="flex items-center gap-1.5 mt-1">
          <Icon name="TrendingUp" size={14} color="#16a34a" />
          <span className="font-caption text-sm" style={{ color: '#16a34a', fontWeight: 600 }}>
            ▲ +{change} pts vs {changePeriod}
          </span>
        </div>
        <p className="font-caption text-xs text-muted-foreground mt-1 text-center">Sample data — demonstration only</p>
      </div>
      {/* Sub-score cards */}
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {SUB_SCORES?.map((s) => (
          <div key={s?.key} className="rounded-xl border border-border bg-background p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <span className="font-caption text-sm font-semibold text-foreground">{s?.label}</span>
              <span className="font-heading font-bold text-lg" style={{ color: s?.color }}>{s?.score}</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${s?.score}%`, background: s?.color }} />
            </div>
            <p className="font-caption text-xs text-muted-foreground">{s?.stat}</p>
            <div className="h-8">
              <Sparkline data={SUB_SCORE_SPARKLINES?.[s?.sparkKey]} color={s?.color} />
            </div>
            <p className="font-caption text-xs text-muted-foreground" style={{ fontSize: '0.65rem' }}>Last 6 quarters</p>
          </div>
        ))}
      </div>
    </div>
  );
}
